import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, notification, Popconfirm, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';

import Auth0UserDetailsContext from '@totem/components/services/remoteAccess/auth0users/details/Auth0UserDetailsContext';
import StepResultsModal from '@totem/components/services/remoteAccess/auth0users/details/StepResultsModal';
import {
  IBAuth0RoleSummary,
  MultiStepResults,
} from '@totem/components/services/remoteAccess/auth0users/details/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintEmpty } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AUTH0_USER_ENDPOINT } from '@totem/utilities/endpoints';

const EMPTY_ID = '000000000000000000000000';

const displayRef = (id?: string, name?: string) => {
  if (!id || id === EMPTY_ID || !name) return '—';
  return name;
};

const Auth0UserRolesTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { user, loading, roles, onRefresh } = useContext(
    Auth0UserDetailsContext,
  );
  const [removingRoleId, setRemovingRoleId] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<MultiStepResults | null>(null);
  const [showResults, setShowResults] = useState<boolean>(false);

  const isRemoteAccessAdmin = permissions.remoteAccessAdmin.hasPermissions(
    userProfile,
    securityGroupCheckConstraintEmpty,
  );
  const isInternal = permissions.internal.hasPermissions(userProfile);

  const handleRemove = (role: IBAuth0RoleSummary) => {
    if (!user || removingRoleId) {
      return;
    }
    setRemovingRoleId(role.id);

    fetch(
      `${AUTH0_USER_ENDPOINT}/${encodeURIComponent(user.id)}/roles/${role.id}`,
      {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: MultiStepResults) => {
        onRefresh();

        if (isInternal) {
          setLastResult(result);

          setShowResults(true);

          return;
        }

        if (result?.success) {
          notification.success({
            message: 'Role removed',
            description: `Removed ${role.name} from ${user.item.email}.`,
          });
        } else {
          notification.error({
            message: 'Error removing role',
            description:
              result?.error ?? 'An error occurred while removing the role.',
            duration: 0,
          });
        }
      })
      .catch(() => {
        notification.error({
          message: 'Error removing role',
          description: 'An error occurred while removing the role.',
          duration: 0,
        });
      })
      .finally(() => setRemovingRoleId(null));
  };

  const columns: ColumnsType<IBAuth0RoleSummary> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => {
        const fromLabel = user?.item?.name || user?.item?.email || '';
        const fromParam = user?.item?.email
          ? `?from=user:${user.item.email}:${fromLabel}`
          : '';

        return (
          <Link
            to={`/dashboard/remoteAccess/auth0role/${record.id}${fromParam}`}
          >
            {record.name}
          </Link>
        );
      },
      sorter: (aRow, bRow) => aRow.name.localeCompare(bRow.name),
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, record) => record.role?.description ?? '',
    },
    {
      title: 'Organization',
      key: 'organization',
      render: (_, record) =>
        displayRef(
          record.hierarchyReference?.organization?.id,
          record.hierarchyReference?.organization?.name,
        ),
      sorter: (aRow, bRow) =>
        (aRow.hierarchyReference?.organization?.name ?? '').localeCompare(
          bRow.hierarchyReference?.organization?.name ?? '',
        ),
    },
    {
      title: 'Region',
      key: 'region',
      render: (_, record) =>
        displayRef(
          record.hierarchyReference?.region?.id,
          record.hierarchyReference?.region?.name,
        ),
      sorter: (aRow, bRow) =>
        (aRow.hierarchyReference?.region?.name ?? '').localeCompare(
          bRow.hierarchyReference?.region?.name ?? '',
        ),
    },
    {
      title: 'Building',
      key: 'building',
      render: (_, record) =>
        displayRef(
          record.hierarchyReference?.building?.id,
          record.hierarchyReference?.building?.name,
        ),
      sorter: (aRow, bRow) =>
        (aRow.hierarchyReference?.building?.name ?? '').localeCompare(
          bRow.hierarchyReference?.building?.name ?? '',
        ),
    },
    {
      title: 'Control System',
      key: 'controlSystem',
      render: (_, record) =>
        displayRef(
          record.hierarchyReference?.controlSystem?.id,
          record.hierarchyReference?.controlSystem?.name,
        ),
      sorter: (aRow, bRow) =>
        (aRow.hierarchyReference?.controlSystem?.name ?? '').localeCompare(
          bRow.hierarchyReference?.controlSystem?.name ?? '',
        ),
    },
    {
      title: 'Top Level',
      key: 'isTopLevel',
      width: 100,
      render: (_, record) =>
        record.isTopLevel ? <Tag color="blue">Yes</Tag> : null,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (sts: string) => (
        <Tag color={sts === 'Active' ? 'success' : 'default'}>{sts}</Tag>
      ),
      sorter: (aRow, bRow) => aRow.status.localeCompare(bRow.status),
    },
    ...(isRemoteAccessAdmin
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 70,
            render: (_: unknown, record: IBAuth0RoleSummary) => (
              <Tooltip title="Remove role">
                <Popconfirm
                  title="Remove this role?"
                  description={`Remove ${record.name} from ${user?.item?.email}?`}
                  okText="Remove"
                  okType="danger"
                  onConfirm={() => handleRemove(record)}
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={removingRoleId === record.id}
                  />
                </Popconfirm>
              </Tooltip>
            ),
          } as ColumnsType<IBAuth0RoleSummary>[number],
        ]
      : []),
  ];

  return (
    <>
      <IBTable<IBAuth0RoleSummary>
        showSorterTooltip
        columns={columns}
        dataSource={roles}
        loading={loading}
        rowKey="id"
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
      <StepResultsModal
        visible={showResults}
        title="Remove Role Result"
        result={lastResult}
        successMessage="Role removed"
        failureMessage="Remove role failed"
        onClose={() => setShowResults(false)}
      />
    </>
  );
};

export default Auth0UserRolesTable;
